import { Component, Input, OnChanges, EventEmitter, Output, ViewChild, ElementRef } from '@angular/core';
import { formatDate } from '@angular/common';

import {
  SelectedSlotDto,
  MeetingDetailsDto,
  CategoryBase,
  Language,
  AvailabilitySlotDetailsDto,
  CategoryDto,
  SupporterEditDto
} from '../../../shared/model';

import 'rxjs/add/operator/finally';

import { SchedulerEvent } from '@progress/kendo-angular-scheduler';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { EditMeetingViewComponent } from './edit-meeting-view/edit-meeting-view.component';
import { SpinnerService } from '../../../shared/spinner/spinner.service';
import { MeetingsService } from '../../../my-meetings-view/my-meetings-view.service';
import { TranslatedNotificationService } from '../../../shared/translation/translated-notification.service';

import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-scheduler-content',
  templateUrl: './scheduler-content.component.html',
  styleUrls: [
    '../../../main-view/second-step/second-step.component.css',
    './scheduler-content.component.css',
    '../../../shared/styles/path-selection.css',
    '../../../shared/styles/lang-color.css']
})
export class SchedulerContentComponent implements OnChanges {
  public selectedDate: Date;
  public startTime = environment.schedulerStartHour;
  public endTime = environment.schedulerEndHour;
  public workDayStart = environment.workDayStartHour;
  public workDayEnd = environment.workDayEndHour;
  public events: SchedulerEvent[];
  public selectedSlot: SelectedSlotDto = <SelectedSlotDto>{};
  public displayWeek = true;

  @Input() filtersCategory: Array<CategoryBase>;
  @Input() filtersLanguage: Array<Language>;
  @Input() availabilitySlots: Array<AvailabilitySlotDetailsDto>;
  @Input() upcomingMeetings: Array<MeetingDetailsDto>;
  @Input() allMeetings: Array<MeetingDetailsDto>;
  @Input() showAvailability = false;
  @Input() allSupporters: SupporterEditDto[];
  @Input() meetingToShow: MeetingDetailsDto = <MeetingDetailsDto>{};
  @Input() allCategories: CategoryDto[];

  @Output() meetingCanceled: EventEmitter<number> = new EventEmitter();
  @Output() meetingChanged: EventEmitter<any> = new EventEmitter();

  @ViewChild('kendoScheduler', { static: false }) kendoScheduler: ElementRef;
  @ViewChild('kendoSchedulerDay', { static: false }) set kendoSchedulerDay(element) {
    if (element) {
      const allDay = this.kendoScheduler.nativeElement.children[0].children[1].children[0].children[0].children;
      const allDayLeft = allDay[0].children[0].children[0].children[1];
      const allDayRight = allDay[1].children[0].children[1];
      allDayLeft.style.display = 'none';
      allDayRight.style.display = 'none';
    }
  }

  @ViewChild('kendoSchedulerWeek', { static: false }) set kendoSchedulerWeek(element) {
    if (element) {
      const allDay = this.kendoScheduler.nativeElement.children[0].children[1].children[0].children[0].children;
      const allDayLeft = allDay[0].children[0].children[0].children[1];
      const allDayRight = allDay[1].children[0].children[1];
      allDayLeft.style.display = 'none';
      allDayRight.style.display = 'none';
    }
  }

  constructor(
    private modalService: NgbModal,
    private spinnerService: SpinnerService,
    private meetingService: MeetingsService,
    private notificationService: TranslatedNotificationService) { }

  ngOnChanges(changes) {
    if ((changes.upcomingMeetings || changes.showAvailability) && !!this.availabilitySlots && !!this.upcomingMeetings) {
      this.mapSlotsFromMeetings();
      if (this.showAvailability) {
        this.mapSlotsFromAvailabilities();
      }
    }

    if (!!changes.meetingToShow && !!changes.meetingToShow.currentValue.Start) {
      this.openEditMeeting(this.meetingToShow.Id, this.meetingToShow);
    }
  }

  private mapSlotsFromMeetings() {
    this.events = this.upcomingMeetings.map(dataItem => (
      <SchedulerEvent>{
        id: dataItem.Id,
        start: this.parseAdjust(dataItem.Start.toString()),
        end: this.parseAdjust(dataItem.End.toString()),
        isAllDay: false,
        title: dataItem.LanguageName,
        description: dataItem.CategoryName,
        dataItem: dataItem.SupporterFirstName + ' ' + dataItem.SupporterLastName,
      }));
  }

  private mapSlotsFromAvailabilities() {
    const mappedSlots = this.availabilitySlots.map(dataItem => (
      <SchedulerEvent>{
        id: dataItem.Id,
        start: this.parseAdjust(dataItem.Start.toString()),
        end: this.parseAdjust(dataItem.End.toString()),
        isAllDay: false,
        title: 'available',
        description: 'available',
        dataItem: dataItem.SupporterFirstName + ' ' + dataItem.SupporterLastName,
      }));

    this.mapSlotsFromMeetings();
    this.events = this.events.concat(mappedSlots);
  }

  getEventType(id: number) {
    return this.upcomingMeetings.find(x => x.Id === id).Type.toString();
  }

  changedSelectedSlot(event, id: number, title: string) {
  }

  formatHeaderDate(date: Date): string {
    return formatDate(date, 'dd/MM', 'en-US');
  }

  formatHeaderDateReturnDay(date: Date): string {
    return formatDate(date, 'EEE', 'en-US').toString();
  }

  dateTemplateString(date: Date): string {
   return 'scheduler_week_day_' + this.formatHeaderDateReturnDay(date).toString();
  }

  openEditMeeting(meetingId: number, meetingData?: MeetingDetailsDto) {
    const meeting = meetingData ? meetingData : this.getMeeting(meetingId);

    if (!!meeting) {
      this.spinnerService.show('spinner');
      this.meetingService.getUserDetailsForMeeting(meetingId)
      .subscribe(res => {
        meeting.UserFirstName = res.FirstName;
        meeting.UserLastName = res.LastName;
        this.openPopup(meeting);
        this.spinnerService.hide('spinner');
      },
      err => {
        this.spinnerService.hide('spinner');
        this.notificationService.showExceptionError(err.error);
        this.openPopup(meeting);
      });
    }
  }

  viewModeChange(displayWeek: boolean) {
    this.displayWeek = displayWeek;
  }

  private getMeeting(id: number): MeetingDetailsDto {
    const meetings = this.upcomingMeetings.map(item => item);

    return meetings.find(m => m.Id === id);
  }

  private openPopup(meeting: MeetingDetailsDto) {
    const modalRef = this.modalService.open(EditMeetingViewComponent);
    modalRef.componentInstance.meeting = meeting;
    modalRef.componentInstance.supporters = this.allSupporters;
    modalRef.componentInstance.allMeetings = this.allMeetings;
    modalRef.componentInstance.allCategories = this.allCategories;
    modalRef.componentInstance.setValues();

    modalRef.result.then((result) => {
      this.spinnerService.show('spinner');
      if (result.HasChanges) {
        this.editMeeting(meeting, result);
      }
      this.spinnerService.hide('spinner');
    }).catch(error => error);
  }

  private editMeeting(meeting: MeetingDetailsDto, result: any) {
    if (result.ToCancel) {
      this.meetingService.cancelMeeting(result.MeetingId)
        .subscribe(() => {
          this.meetingCanceled.emit(result.MeetingId);
          this.spinnerService.hide('spinner');
        },
          (err) => {
            this.spinnerService.hide('spinner');
            this.notificationService.showExceptionError(err.error);
          });
    } else if (!this.supporterHasCollidingMeeting(meeting.Start, result.SupporterId, meeting.Id)) {
      this.changeSupporterForMeeting(meeting, result.SupporterId, result.CategoryId);
    } else {
      this.notificationService.showMsgError('error.supporter_is_busy', 'Selected supporter has another meeting during this time.');
    }
  }

  private changeSupporterForMeeting(meeting: MeetingDetailsDto, supporterId: string, categoryId: string) {
    const newSupporter = this.allSupporters.find(s => s.Id === supporterId);
    meeting.SupporterId = newSupporter.Id;
    meeting.SupporterFirstName = newSupporter.FirstName;
    meeting.SupporterLastName = newSupporter.LastName;
    meeting.CategoryId = categoryId;
    meeting.CategoryName = this.allCategories.find(c => c.Id === categoryId).Name;

    this.meetingService.updateMeeting(meeting)
      .subscribe((newSlotId) => {
        meeting.AvailabilityId = newSlotId;
        this.spinnerService.hide('spinner');
        this.notificationService.showMsgSuccess('success.change_supporter', 'Successfully changed supporter for meeting');
        this.meetingChanged.emit();
      },
        (err) => {
          this.spinnerService.hide('spinner');
          this.notificationService.showExceptionError(err.error);
        });
  }

  private supporterHasCollidingMeeting(start: Date, id: string, meetingId: number): boolean {
    const collidingMeeting = this.upcomingMeetings.find(m => m.SupporterId === id && m.Id !== meetingId && m.Start === start);

    return !!collidingMeeting;
  }

  private parseAdjust = (eventDate: string): Date => {
    const date = new Date(eventDate + 'Z');
    return new Date(date);
  }
}
